import { useEffect, useRef, useState } from 'react';
import { useWorkspace } from './workspace';

export type AITask = 'committee' | 'scanner' | 'strategy' | 'backtest' | 'portfolio' | 'risk' | 'execution' | 'chat';
export function useAI() {
  const { requireAuth } = useWorkspace();
  const [busy, setBusy] = useState(false), [text, setText] = useState(''), [result, setResult] = useState<any>(null), [error, setError] = useState('');
  const controller = useRef<AbortController | null>(null);
  useEffect(() => () => controller.current?.abort(), []);
  const run = async (payload: { task: AITask; symbol?: string; question: string; source_id?: string; thread_id?: string }) => {
    if (!requireAuth() || busy) return null;
    setBusy(true); setText(''); setResult(null); setError(''); controller.current = new AbortController();
    let completed: any = null;
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/v1/ai/analyze`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload), signal: controller.current.signal });
      if (!response.ok) { const e = await response.json(); throw new Error(typeof e.detail === 'string' ? e.detail : 'AI request failed validation.'); }
      if (!response.body) throw new Error('Streaming is unavailable in this browser.');
      const reader = response.body.getReader(), decoder = new TextDecoder(); let buffer = '';
      while (true) {
        const { done, value } = await reader.read(); if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let boundary = buffer.indexOf('\n\n');
        while (boundary !== -1) {
          const chunk = buffer.slice(0, boundary); buffer = buffer.slice(boundary + 2);
          const event = chunk.split('\n').find(l => l.startsWith('event: '))?.slice(7);
          const dataLine = chunk.split('\n').find(l => l.startsWith('data: '));
          if (dataLine) { const data = JSON.parse(dataLine.slice(6));
            if (event === 'delta') setText(t => t + data.text);
            if (event === 'complete') { completed = data; setResult(data); }
            if (event === 'error') throw new Error(data.message);
          }
          boundary = buffer.indexOf('\n\n');
        }
      }
      if (!completed) throw new Error('AI stream ended before validation. No completed analysis was accepted.');
      return completed;
    } catch (e: any) { setError(e.name === 'AbortError' ? 'Analysis cancelled.' : e.message || 'AI analysis failed. No fallback model was used.'); return null; }
    finally { setBusy(false); controller.current = null; }
  };
  return { busy, text, result, error, run, cancel: () => controller.current?.abort() };
}